import { nullToEmptyString } from '@/util/zod/transforms'
import { z } from 'zod'

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
}

export enum OrganizationSubscriptionStatus {
  PAID = 'paid',
  TRIALING = 'trialing',
  CANCELED = 'canceled',
}

// ================================ Organization User ================================

/**
 * Represents a user in an organization.
 *
 * Matches BE ClientOrganizationUserRead, a client-facing version of UserRead which
 * omits sensitive fields and adds fields for client-specific data.
 */
export const ClientOrganizationUserReadSchema = z.object({
  id: z.string(),
  email: z.string().nullable().transform(nullToEmptyString),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  organization_id: z.string().nullable(),
  firebase_id: z.string().nullable(),
  role: z.nativeEnum(UserRole).nullable(),
  has_access: z.boolean().nullable(),
  is_organization_payer: z.boolean(),
})
export type ClientOrganizationUserRead = z.infer<typeof ClientOrganizationUserReadSchema>

/**
 * Member-visible organization directory row for matter invite typeahead.
 *
 * Matches BE OrganizationDirectoryUserRead from GET /organization/users.
 */
export const OrganizationDirectoryUserReadSchema = z.object({
  user_id: z.string(),
  display_name: z.string().nullable(),
  email: z.string().nullable(),
  org_role: z.nativeEnum(UserRole).nullable(),
})
export type OrganizationDirectoryUserRead = z.infer<typeof OrganizationDirectoryUserReadSchema>

export const GetOrganizationDirectoryUsersResponseSchema = z.object({
  users: z.array(OrganizationDirectoryUserReadSchema),
})
export type GetOrganizationDirectoryUsersResponse = z.infer<typeof GetOrganizationDirectoryUsersResponseSchema>

// ================================ User Invite ================================

/**
 * Represents an invite to add a user to an organization.
 */
export const UserInviteReadSchema = z.object({
  id: z.string(),
  invitee_id: z.string().nullable(),
  invitee_email: z.string().nullable().transform(nullToEmptyString),
  organization_id: z.string(),
  complete: z.boolean(),
  created_at: z.string().optional(),
  modified_on: z.string().optional(),
})
export type UserInviteRead = z.infer<typeof UserInviteReadSchema>

/**
 * The request to create an invite to a user in an organization.
 */
export const CreateUserInviteRequestSchema = z.object({
  invite: z.object({
    invitee_email: z.string().email(),
    organization_id: z.string().uuid(),
  }),
})

export type CreateUserInviteRequest = z.infer<typeof CreateUserInviteRequestSchema>

/**
 * What happened to the invite email after the invite itself was persisted.
 * Mirrors the backend InviteEmailStatus enum.
 */
export enum InviteEmailStatus {
  SENT = 'sent',
  // Non-prod environments only email @paxton.ai / allowlisted addresses; the email went to the test inbox
  REDIRECTED_TEST_INBOX = 'redirected_test_inbox',
  FAILED = 'failed',
}

/**
 * The response from creating an invite. The invite may exist even when its email did not go out.
 *
 * email_status is parsed as a loose string for forward compat: a future backend status passes
 * through unchanged (the UI treats anything !== sent neutrally instead of claiming success);
 * only a missing/invalid field falls back to sent.
 */
export const CreateUserInviteResponseSchema = z.object({
  invite: UserInviteReadSchema,
  email_status: z.string().catch(InviteEmailStatus.SENT),
})

export type CreateUserInviteResponse = z.infer<typeof CreateUserInviteResponseSchema>

/**
 * The response from resending an invite email. A send failure is a 502, so a 2xx here
 * is either sent or redirected to the non-prod test inbox. Same loose email_status
 * parsing as CreateUserInviteResponseSchema.
 */
export const ResendInviteResponseSchema = z.object({
  detail: z.string(),
  email_status: z.string().catch(InviteEmailStatus.SENT),
})

export type ResendInviteResponse = z.infer<typeof ResendInviteResponseSchema>

// ================================ Organization ================================

/**
 * Represents an organization.
 *
 * Matches BE ClientOrganizationRead, a client-facing version of OrganizationRead which
 * omits sensitive fields and adds fields for client-specific data.
 */
export const ClientOrganizationReadSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  seats: z.number(),
  has_access: z.boolean(),
  subscription_status: z.nativeEnum(OrganizationSubscriptionStatus),
  is_organization_payer: z.boolean(),
})
export type ClientOrganizationRead = z.infer<typeof ClientOrganizationReadSchema>

/**
 * Represents an organization with details.
 *
 * Matches BE OrganizationWithDetailsRead, a version of ClientOrganizationRead which
 * includes users and invites.
 */
export const OrganizationWithDetailsReadSchema = ClientOrganizationReadSchema.extend({
  users: z.array(ClientOrganizationUserReadSchema),
  invites: z.array(UserInviteReadSchema),
})

/**
 * Represents a response from the get organization with details endpoint.
 */
export const GetOrganizationWithDetailsResponseSchema = z.object({
  organization: OrganizationWithDetailsReadSchema,
})

export type GetOrganizationWithDetailsResponse = z.infer<typeof GetOrganizationWithDetailsResponseSchema>

/**
 * The response from the get organization endpoint.
 */
export const GetOrganizationResponseSchema = z.object({
  organization: ClientOrganizationReadSchema,
})

export type GetOrganizationResponse = z.infer<typeof GetOrganizationResponseSchema>

/**
 * The request to create a an organization.
 *
 * Matches BE CreateSelfServeOrganizationRequest - In the BE, invites is an array of the old
 * UserInviteSchema, all fields except invitee_email are optional and ignored. Here we simplify
 * to capture only the field that is actually required and sent by the FE.
 */
export const CreateSelfServeOrganizationRequestSchema = z.object({
  organization: z.object({
    name: z.string(),
    invites: z.array(z.object({ invitee_email: z.string().email() })),
  }),
})

export type CreateSelfServeOrganizationRequest = z.infer<typeof CreateSelfServeOrganizationRequestSchema>

/**
 * The response from the create organization endpoint.
 */
export const CreateOrganizationResponseSchema = z.object({
  organization: ClientOrganizationReadSchema,
})

export type CreateOrganizationResponse = z.infer<typeof CreateOrganizationResponseSchema>
